import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FileTextIcon, ShieldIcon, FileIcon, ScaleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Policies() {
  const policies = [{
    id: 'privacy',
    title: 'Privacy & Cookie Policy',
    icon: <ShieldIcon size={24} className="text-[#3AA6F9]" data-id="element-1614" />,
    description: 'Learn how we collect, use, and protect your personal data in compliance with GDPR regulations.',
    link: '/privacy'
  }, {
    id: 'cancellation',
    title: 'Cancellation Policy',
    icon: <FileTextIcon size={24} className="text-[#3AA6F9]" data-id="element-1615" />,
    description: 'Understand our refund windows, notice periods, and how we handle change requests.',
    link: '/cancellation'
  }, {
    id: 'terms',
    title: 'Terms & Conditions',
    icon: <ScaleIcon size={24} className="text-[#3AA6F9]" data-id="element-1616" />,
    description: 'Review our complete terms covering project scope, payments, intellectual property, and more.',
    link: '/terms'
  }];
  return <>
      <Helmet data-id="element-1617">
        <title data-id="element-1618">Policies | Bright Pixxel Studio</title>
        <meta name="description" content="Read our Privacy & Cookie Policy, Cancellation Policy and Terms & Conditions — clear, GDPR-compliant and in plain English for your peace of mind." data-id="element-1619" />
        <link rel="canonical" href="https://bright-pixxel.com/policies" data-id="element-1620" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-1621" />
        <meta property="og:type" content="website" data-id="element-1622" />
        <meta property="og:title" content="Policies | Bright Pixxel Studio" data-id="element-1623" />
        <meta property="og:description" content="Read our Privacy & Cookie Policy, Cancellation Policy and Terms & Conditions — clear, GDPR-compliant and in plain English for your peace of mind." data-id="element-1624" />
        <meta property="og:url" content="https://bright-pixxel.com/policies" data-id="element-1625" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-1626" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1627" />
        <meta property="og:image:width" content="1200" data-id="element-1628" />
        <meta property="og:image:height" content="630" data-id="element-1629" />
        <meta property="og:image:type" content="image/png" data-id="element-1630" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Policies" data-id="element-1631" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-1632" />
        <meta name="twitter:title" content="Policies | Bright Pixxel Studio" data-id="element-1633" />
        <meta name="twitter:description" content="Read our Privacy & Cookie Policy, Cancellation Policy and Terms & Conditions — clear, GDPR-compliant and in plain English for your peace of mind." data-id="element-1634" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1635" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Policies" data-id="element-1636" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-4 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1637">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1638">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1639">
            <motion.span initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1640">
              Legal Information
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1641">
              Policies & Legal Documents
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8" data-id="element-1642">
              We believe in transparency. Review our policies to understand how
              we handle your data, project terms, and more.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Policies Section */}
      <section className="py-4 md:py-6 bg-white dark:bg-[#1E1E1E]" data-id="element-1643">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1644">
          <div className="max-w-5xl mx-auto" data-id="element-1645">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1646">
              {policies.map((policy, index) => <motion.div key={policy.id} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-1647">
                  <Link to={policy.link} className="block p-6" data-id="element-1648">
                    <div className="w-12 h-12 rounded-full bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 flex items-center justify-center mb-4" data-id="element-1649">
                      {policy.icon}
                    </div>
                    <h2 className="text-xl font-semibold mb-3" data-id="element-1650">
                      {policy.title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-1651">
                      {policy.description}
                    </p>
                    <span className="text-[#3AA6F9] font-medium hover:underline" data-id="element-1652">
                      Read Policy →
                    </span>
                  </Link>
                </motion.div>)}
            </div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="mt-16 p-6 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-1653">
              <div className="flex items-start" data-id="element-1654">
                <div className="w-10 h-10 rounded-full bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1655">
                  <FileIcon size={20} className="text-[#3AA6F9]" data-id="element-1656" />
                </div>
                <div data-id="element-1657">
                  <h3 className="text-lg font-semibold mb-2" data-id="element-1658">
                    Need a Custom Agreement?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1659">
                    If you require a custom agreement or have specific legal
                    requirements for your project, please contact us. We're
                    happy to work with your legal team to create an agreement
                    that meets your needs.
                  </p>
                  <div className="mt-4" data-id="element-1660">
                    <Link to="/contact" className="text-[#3AA6F9] font-medium hover:underline" data-id="element-1661">
                      Contact Us →
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* GDPR Compliance Section */}
      <section className="py-4 md:py-6 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1662">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1663">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1664">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1665">
              GDPR Compliance
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1666">
              At Bright Pixxel Studio, we take data protection seriously. We
              comply with the General Data Protection Regulation (GDPR) and
              implement appropriate measures to protect your personal
              information.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl border border-gray-200 dark:border-gray-700 text-left" data-id="element-1667">
              <h3 className="text-lg font-semibold mb-4" data-id="element-1668">
                Our GDPR Commitment
              </h3>
              <ul className="space-y-3" data-id="element-1669">
                <li className="flex items-start" data-id="element-1670">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#3AA6F9] mt-1.5 mr-2" data-id="element-1671"></div>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1672">
                    <span className="font-medium" data-id="element-1673">Transparency:</span> We
                    clearly explain how we collect and use your data
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1674">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#3AA6F9] mt-1.5 mr-2" data-id="element-1675"></div>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1676">
                    <span className="font-medium" data-id="element-1677">Data Minimization:</span> We
                    only collect the data we need for specific purposes
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1678">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#3AA6F9] mt-1.5 mr-2" data-id="element-1679"></div>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1680">
                    <span className="font-medium" data-id="element-1681">Security:</span> We implement
                    appropriate technical and organizational measures
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1682">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#3AA6F9] mt-1.5 mr-2" data-id="element-1683"></div>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1684">
                    <span className="font-medium" data-id="element-1685">Your Rights:</span> We respect
                    your right to access, correct, delete, and export your data
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1686">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#3AA6F9] mt-1.5 mr-2" data-id="element-1687"></div>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1688">
                    <span className="font-medium" data-id="element-1689">Compliance:</span> We ensure
                    our third-party processors are GDPR compliant
                  </p>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}